import {
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Typography,
} from '@mui/material';
import { tableContainer, tableHeadCell } from '../../styles/pageStyles';

/**
 * Tabela padronizada — encapsula o boilerplate de TableContainer/Paper/TableHead.
 *
 * Gerencia o estado de carregamento e vazio inline.
 * O estado vazio completo (EmptyState) é responsabilidade do caller, que deve
 * checar `items.length === 0` ANTES de renderizar a tabela.
 * Use `isEmpty` apenas para o caso de "sem resultados após filtro", onde a tabela
 * já está presente na página mas os dados filtrados são zero.
 *
 * @param {Array}     columns         - Array de { id, label, align?, width?, sortable? }
 * @param {boolean}   loading         - Exibe spinner inline no corpo da tabela
 * @param {boolean}   isEmpty         - Exibe mensagem de "sem resultados" no corpo
 * @param {string}    emptyMessage    - Texto do estado vazio (padrão: "Nenhum registro encontrado")
 * @param {string}    sortBy          - Coluna ativa de ordenação
 * @param {'asc'|'desc'} sortDirection - Direção da ordenação
 * @param {Function}  onSort          - Callback (columnId) => void
 * @param {ReactNode} children        - <TableRow> elements do corpo
 * @param {Object}    sx              - sx extra passado ao TableContainer
 *
 * @example
 * <StandardTable
 *   columns={[
 *     { id: 'name',    label: 'Nome',   sortable: true },
 *     { id: 'status',  label: 'Status', align: 'center' },
 *     { id: 'actions', label: 'Ações',  align: 'right' },
 *   ]}
 *   loading={tableLoading}
 *   isEmpty={appointments.length === 0}
 *   emptyMessage="Nenhum agendamento no período"
 *   sortBy={sortBy}
 *   sortDirection={sortDirection}
 *   onSort={handleSort}
 * >
 *   {items.map(item => (
 *     <TableRow key={item.id} hover>
 *       <TableCell>{item.name}</TableCell>
 *     </TableRow>
 *   ))}
 * </StandardTable>
 */
export default function StandardTable({
  columns = [],
  loading = false,
  isEmpty = false,
  emptyMessage = 'Nenhum registro encontrado',
  sortBy,
  sortDirection = 'asc',
  onSort,
  children,
  sx,
}) {
  const colSpan = columns.length;

  return (
    <TableContainer component={Paper} variant="outlined" sx={[tableContainer, { overflowX: 'auto', maxWidth: '100%' }, sx]}>
      <Table size="small">
        <TableHead sx={{ bgcolor: 'grey.50' }}>
          <TableRow>
            {columns.map((col) => (
              <TableCell
                key={col.id}
                sx={tableHeadCell}
                align={col.align}
                width={col.width}
              >
                {col.sortable && onSort ? (
                  <TableSortLabel
                    active={sortBy === col.id}
                    direction={sortBy === col.id ? sortDirection : 'asc'}
                    onClick={() => onSort(col.id)}
                  >
                    {col.label}
                  </TableSortLabel>
                ) : (
                  col.label
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={colSpan} align="center" sx={{ py: 4 }}>
                <CircularProgress size={24} />
              </TableCell>
            </TableRow>
          ) : isEmpty ? (
            <TableRow>
              <TableCell colSpan={colSpan} align="center" sx={{ py: 4 }}>
                <Typography color="text.secondary" variant="body2">
                  {emptyMessage}
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            children
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
